import dayjs from 'dayjs';
import sortBy from 'lodash/sortBy';
import uniq from 'lodash/uniq';
import * as React from 'react';
import AutoSizer from 'react-virtualized/dist/es/AutoSizer';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ReferenceArea,
  ReferenceLine,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { IZScoreResult } from '../../../../data/providers/reports/zscoreReportProvider';
import { ISize } from '../../../../data/types';
import { Column } from '../../../layout/Column';
import { Row } from '../../../layout/Row';
import { Card } from '../../../shared/card/Card';
import { GraphContainer } from '../../../shared/graphs/GraphContainer';
import { legendFormatter } from '../../../shared/graphs/legendFormatter';
import { Notable } from '../../../shared/pdf/Notable';
import { EPdfMode, isPdf, shouldInvert } from '../../../shared/pdf/Pdf';
import { colors } from '../../colors';
import { abbreviateMetricLabel } from '../../utils/abbreviateMetricLabel';
import { Tooltip as CustomTooltip } from './Tooltip';

interface IProps {
  results: IZScoreResult[];
  pdfMode: EPdfMode;
  colors: string[];
  scaled: boolean;
}

interface IDataPoint {
  [metricKey: string]: number;
  Date: number;
}

interface IGraphData {
  labels: string[];
  data: IDataPoint[];
}

export const ZScoreGraph = (props: IProps): React.ReactElement => {
  const data = React.useMemo((): IGraphData => {
    const labels: string[] = [];

    const mData = props.results.map((result: IZScoreResult): IDataPoint => {
      labels.push(...Object.keys(result.scores));
      const scores = Object.keys(result.scores).reduce(
        (
          acc: { [metricKey: string]: number },
          curr: string
        ): { [metricKey: string]: number } => {
          acc[abbreviateMetricLabel(curr)] = result.scores[curr].score;
          return acc;
        },
        {}
      );
      return {
        Date: result.timestamp,
        ...scores,
      };
    });

    const sortedData = sortBy(mData, 'Date', 'asc');

    return {
      labels: uniq(labels),
      data: sortedData,
    };
  }, [JSON.stringify(props.results)]);

  return (
    <Row>
      <Notable pdf={isPdf(props.pdfMode)}>
        <Card
          noTopRadius
          inverse={shouldInvert(props.pdfMode)}
          style={{
            padding: '0px',
            minHeight: 'var(--graph-min-height)',
          }}
        >
          <Column>
            <GraphContainer inverse={shouldInvert(props.pdfMode)}>
              <div
                style={{
                  height: '100%',
                  width: '100%',
                }}
                className='graph-parent'
              >
                <AutoSizer>
                  {(size: ISize): React.ReactElement => {
                    return (
                      <BarChart
                        width={size.width}
                        height={size.height}
                        data={data.data}
                        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid
                          vertical={false}
                          horizontal
                          stroke='#ccc'
                          strokeOpacity={0.25}
                        />

                        <ReferenceLine
                          y={props.scaled ? 50 : 0}
                          ifOverflow='extendDomain'
                          stroke='white'
                          strokeWidth={1}
                          strokeOpacity={1}
                        />
                        <ReferenceArea
                          y1={props.scaled ? 50 : 0}
                          ifOverflow='extendDomain'
                          stroke='green'
                          fill='green'
                          fillOpacity={0.2}
                          strokeOpacity={0}
                        />
                        <ReferenceArea
                          y2={props.scaled ? 50 : 0}
                          y1={props.scaled ? 0 : -4}
                          ifOverflow='extendDomain'
                          stroke='red'
                          fill='red'
                          fillOpacity={0.2}
                          strokeOpacity={0}
                        />
                        <XAxis
                          dataKey='Date'
                          axisLine={false}
                          tickLine={false}
                          tick={{ fill: 'var(--color-text' }}
                          tickFormatter={tickFormatter}
                        />
                        <YAxis
                          domain={props.scaled ? [0, 100] : [-4, 4]}
                          axisLine={false}
                          tickLine={false}
                          tick={{ fill: 'var(--color-text' }}
                        />
                        <Tooltip
                          cursor={false}
                          content={CustomTooltip}
                          formatter={tickFormatter}
                        />
                        {data.labels.map(
                          (m: string, i: number): React.ReactElement => {
                            return (
                              <Bar
                                key={i}
                                dataKey={abbreviateMetricLabel(m)}
                                fill={colors[i]}
                              />
                            );
                          }
                        )}

                        <Legend
                          iconType='circle'
                          formatter={(value) =>
                            legendFormatter(value, props.pdfMode)
                          }
                        />
                      </BarChart>
                    );
                  }}
                </AutoSizer>
              </div>
            </GraphContainer>
          </Column>
        </Card>
      </Notable>
    </Row>
  );
};
const tickFormatter = (tick: number): string =>
  tick > 12875256000 ? dayjs.utc(Number(tick)).format('M/D') : tick.toString();
